import { useState, useEffect, useCallback } from 'react';
import reviewCloudApiClient from '../api/reviewCloudApiClient';

export interface PerformSparkCalendarTask {
  taskName: string;
  taskType: string;
  phaseLabel: string;
  taskDate: string; // YYYY-MM-DD
  actionUrl?: string;
  employeeName?: string;
}

interface UsePerformSparkCalendarTasksResult {
  tasks: PerformSparkCalendarTask[];
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

export const usePerformSparkCalendarTasks = (
  year: number,
  month: number,
): UsePerformSparkCalendarTasksResult => {
  const [tasks, setTasks] = useState<PerformSparkCalendarTask[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchTasks = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // month is 0-indexed, build YYYY-MM-DD for first and last day
      const start = `${year}-${String(month + 1).padStart(2, '0')}-01`;
      const lastDay = new Date(year, month + 1, 0).getDate();
      const end = `${year}-${String(month + 1).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;

      const response = await reviewCloudApiClient.get('/portal/calendar-tasks', {
        params: { start, end },
      });

      const payload: PerformSparkCalendarTask[] =
        response.data?.data?.payload || [];
      setTasks(payload);
    } catch (err: any) {
      if (err.response?.status === 401) {
        setError('Session expired');
      } else if (err.response?.status === 403) {
        setError('No permission');
      } else {
        setError('Failed to load calendar tasks');
      }
      setTasks([]);
    } finally {
      setLoading(false);
    }
  }, [year, month]);

  useEffect(() => {
    fetchTasks();
  }, [fetchTasks]);

  return { tasks, loading, error, refetch: fetchTasks };
};
